// Utility functions for geocoding and privacy

const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org';
const NOMINATIM_HEADERS = {
  'User-Agent': 'Alliance-Solidarite/1.0' // Required by Nominatim usage policy
};

/**
 * Search addresses using Nominatim OpenStreetMap
 * @param {string} query - Search query
 * @returns {Promise<Array>} Array of address suggestions
 */
export const searchAddresses = async (query) => {
  if (!query || query.length < 3) {
    return [];
  }

  try {
    const params = new URLSearchParams({
      q: query,
      format: 'json',
      addressdetails: '1',
      limit: '5',
      countrycodes: 'fr' // Limit to France
    });

    const response = await fetch(`${NOMINATIM_BASE_URL}/search?${params}`, {
      headers: NOMINATIM_HEADERS
    });

    if (!response.ok) {
      console.error('Nominatim API error:', response.status);
      return [];
    }

    const data = await response.json();
    
    // Format results
    return data.map(item => ({
      display_name: item.display_name,
      address: item.address,
      lat: parseFloat(item.lat),
      lon: parseFloat(item.lon),
      formatted: formatNominatimAddress(item.address)
    }));
  } catch (error) {
    console.error('Error fetching address suggestions:', error);
    return [];
  }
};

/**
 * Format Nominatim address object into readable string
 * @param {object} address - Nominatim address object
 * @returns {string} Formatted address
 */
const formatNominatimAddress = (address) => {
  const parts = [];
  
  if (address.house_number && address.road) {
    parts.push(`${address.house_number} ${address.road}`);
  } else if (address.road) {
    parts.push(address.road);
  }
  
  if (address.postcode && address.city) {
    parts.push(`${address.postcode} ${address.city}`);
  } else if (address.city || address.town || address.village) {
    parts.push(address.city || address.town || address.village);
  }
  
  return parts.join(', ') || address.display_name;
};

/**
 * Geocode a full address to coordinates
 * @param {string} address - Address to geocode
 * @returns {Promise<object>} { lat, lon } or null
 */
export const geocodeAddress = async (address) => {
  if (!address) return null;

  try {
    const params = new URLSearchParams({
      q: address,
      format: 'json',
      limit: '1',
      countrycodes: 'fr'
    });

    const response = await fetch(`${NOMINATIM_BASE_URL}/search?${params}`, {
      headers: NOMINATIM_HEADERS
    });

    if (!response.ok) return null;

    const data = await response.json();
    if (data.length === 0) return null;

    return {
      lat: parseFloat(data[0].lat),
      lon: parseFloat(data[0].lon)
    };
  } catch (error) {
    console.error('Error geocoding address:', error);
    return null;
  }
};

/**
 * Extract city from full address
 * Example: "15 Rue des Jardins, 94230 Cachan" => "Cachan"
 */
export const extractCity = (address) => {
  if (!address) return null;
  
  // Try to extract city from common French address formats
  // Format: "Street, PostalCode City"
  const parts = address.split(',');
  if (parts.length >= 2) {
    const lastPart = parts[parts.length - 1].trim();
    // Remove postal code (5 digits)
    const city = lastPart.replace(/^\d{5}\s+/, '').trim();
    return city;
  }
  
  return address;
};

/**
 * Get approximate coordinates for a city (center of city)
 * In production, use a real geocoding API
 */
export const getCityCoordinates = (city) => {
  // Mock city coordinates (French cities)
  const cityCoords = {
    'Cachan': { lat: 48.7945, lng: 2.3315 },
    'Paris': { lat: 48.8566, lng: 2.3522 },
    'Lyon': { lat: 45.7640, lng: 4.8357 },
    'Marseille': { lat: 43.2965, lng: 5.3698 },
    'Toulouse': { lat: 43.6047, lng: 1.4442 },
    'Nice': { lat: 43.7102, lng: 7.2620 },
    'Bordeaux': { lat: 44.8378, lng: -0.5792 },
    'Lille': { lat: 50.6292, lng: 3.0573 },
    // Add more cities as needed
  };
  
  // Return city coords or approximate from postal code
  if (cityCoords[city]) {
    return cityCoords[city];
  }
  
  // Default to Paris if city not found
  return { lat: 48.8566, lng: 2.3522 };
};

/**
 * Get coordinates based on privacy settings
 * @param {string} address - Full address
 * @param {boolean} showAddress - Privacy setting
 * @returns {object} { lat, lng, isApproximate }
 */
export const getDisplayCoordinates = (address, showAddress = true) => {
  if (showAddress) {
    // Show exact location (in production, use real geocoding)
    // For now, return mock exact coordinates
    return {
      lat: 48.7938,
      lng: 2.3297,
      isApproximate: false,
      displayText: address
    };
  } else {
    // Show only city location
    const city = extractCity(address);
    const coords = getCityCoordinates(city);
    return {
      ...coords,
      isApproximate: true,
      displayText: city || 'Localisation approximative'
    };
  }
};

/**
 * Format address for public display based on privacy
 * @param {string} address - Full address
 * @param {boolean} showAddress - Privacy setting
 * @returns {string} Formatted address
 */
export const formatPublicAddress = (address, showAddress = true) => {
  if (showAddress) {
    return address;
  }
  
  const city = extractCity(address);
  return city ? `📍 ${city} (localisation approximative)` : 'Localisation masquée';
};
